import * as Tone from 'tone';
import { h, clear } from './dom.js';

export function mountTransportBar(ctx, slot) {
    clear(slot);

    const playBtn = h('button.btn.btn-primary', { onClick: togglePlay }, '▶ Play');
    const stopBtn = h('button.btn', { onClick: () => ctx.scheduler.stop() }, '■ Stop');
    const clearBtn = h('button.btn', { onClick: clearActivePattern }, 'Clear');

    const tempoLabel = h('span.tempo-value', {}, String(Math.round(ctx.scheduler.getBPM())));
    const tempo = h('input', {
        type: 'range', min: 40, max: 220, step: 1,
        value: Math.round(ctx.scheduler.getBPM()),
        onInput: (e) => {
            const bpm = parseInt(e.target.value, 10);
            tempoLabel.textContent = String(bpm);
            ctx.scheduler.setBPM(bpm);
        },
    });

    // Tap-tempo: average the interval over the last few taps. Reset the
    // window if the user pauses for more than 2 s.
    const tapTimes = [];
    const tapBtn = h('button.btn.btn-tap', { title: 'Tap to set tempo' }, 'TAP');
    tapBtn.addEventListener('click', () => {
        const now = performance.now();
        if (tapTimes.length && now - tapTimes[tapTimes.length - 1] > 2000) tapTimes.length = 0;
        tapTimes.push(now);
        if (tapTimes.length > 5) tapTimes.shift();
        if (tapTimes.length >= 2) {
            const avgMs = (tapTimes[tapTimes.length - 1] - tapTimes[0]) / (tapTimes.length - 1);
            const bpm = Math.max(40, Math.min(220, Math.round(60000 / avgMs)));
            tempo.value = bpm;
            tempoLabel.textContent = String(bpm);
            ctx.scheduler.setBPM(bpm);
        }
        tapBtn.classList.add('flash');
        setTimeout(() => tapBtn.classList.remove('flash'), 80);
    });

    const masterLabel = h('span.vol-value', {}, String(Math.round(ctx.mixer.getMaster().volume.value)));
    const master = h('input', {
        type: 'range', min: -40, max: 0, step: 1,
        value: Math.round(ctx.mixer.getMaster().volume.value),
        onInput: (e) => {
            const db = parseInt(e.target.value, 10);
            masterLabel.textContent = String(db);
            ctx.mixer.getMaster().volume.value = db;
        },
    });

    // Swing: Tone.Transport.swing pushes every other 16th later. 0 = straight,
    // 0.5 = jazzy, 1 = full triplet.
    Tone.Transport.swingSubdivision = '16n';
    const swingLabel = h('span.tempo-value', {}, `${Math.round((Tone.Transport.swing || 0) * 100)}`);
    const swing = h('input', {
        type: 'range', min: 0, max: 75, step: 1,
        value: Math.round((Tone.Transport.swing || 0) * 100),
        title: 'Swing (% of triplet feel applied to off-beat 16ths)',
        onInput: (e) => {
            const pct = parseInt(e.target.value, 10);
            swingLabel.textContent = String(pct);
            Tone.Transport.swing = pct / 100;
        },
    });

    const lengthSel = h('select', {
        onChange: (e) => {
            const len = parseInt(e.target.value, 10);
            const p = ctx.patterns.getActive();
            p.length = len;
            // Resize all tracks to new length.
            for (const k of Object.keys(p.tracks)) {
                const t = p.tracks[k];
                const next = Array(len).fill(0);
                for (let i = 0; i < Math.min(t.steps.length, len); i++) next[i] = t.steps[i];
                t.steps = next;
            }
            ctx.bus.emit('pattern:lengthChanged', p);
        },
    }, [
        h('option', { value: '8' }, '8'),
        h('option', { value: '16', selected: true }, '16'),
        h('option', { value: '32' }, '32'),
    ]);
    syncLengthFromActive();

    slot.appendChild(h('div.brand', {}, 'Music Maker'));
    slot.appendChild(h('div.transport', {}, [playBtn, stopBtn, clearBtn]));
    slot.appendChild(h('div.controls', {}, [
        h('label', {}, ['Tempo', tempo, tempoLabel, 'BPM', tapBtn]),
        h('label', {}, ['Swing', swing, swingLabel, '%']),
        h('label', {}, ['Master', master, masterLabel, 'dB']),
        h('label', {}, ['Steps', lengthSel]),
    ]));

    // Spacebar = play/pause when no input/select/button is focused.
    document.addEventListener('keydown', (e) => {
        if (e.key !== ' ') return;
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return;
        e.preventDefault();
        togglePlay();
    });

    ctx.bus.on('transport:started', () => { playBtn.textContent = '❚❚ Pause'; playBtn.classList.add('playing'); });
    ctx.bus.on('transport:paused', () => { playBtn.textContent = '▶ Play'; playBtn.classList.remove('playing'); });
    ctx.bus.on('transport:stopped', () => { playBtn.textContent = '▶ Play'; playBtn.classList.remove('playing'); });
    ctx.bus.on('pattern:activeChanged', syncLengthFromActive);
    // Sync the tempo slider when something else changes BPM (e.g. tap-tempo,
    // a project load, or another script).
    ctx.bus.on('transport:bpmChanged', (bpm) => {
        const rounded = Math.round(bpm);
        if (parseInt(tempo.value, 10) !== rounded) {
            tempo.value = rounded;
            tempoLabel.textContent = String(rounded);
        }
    });
    ctx.bus.on('project:loaded', syncLengthFromActive);

    function togglePlay() {
        if (ctx.scheduler.isPlaying()) ctx.scheduler.pause();
        else ctx.scheduler.play();
    }

    function clearActivePattern() {
        if (!confirm('Clear all steps in this pattern?')) return;
        const p = ctx.patterns.getActive();
        for (const t of Object.values(p.tracks)) t.steps.fill(0);
        ctx.bus.emit('pattern:cleared', p);
    }

    function syncLengthFromActive() {
        const p = ctx.patterns.getActive();
        if (p) lengthSel.value = String(p.length);
    }
}
